import {
  PayoutPreview,
  useEarningInvoice,
} from 'organization/Earning/EarningInvoiceProvider'
import {useOrganizationRoutes} from 'organization/OrganizationRoutes'
import {useStripeConnection} from 'organization/OrganizationSettings/StripeConnectionProvider'
import {useOrganization} from 'organization/providers/OrganizationProvider'
import React, {useState} from 'react'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import styled from 'styled-components'
import {useI18n} from 'i18n/I18nContext'

export function InvoiceIssue() {
  const {t} = useI18n()
  const {organization} = useOrganization()

  const {payoutPreview, issueInvoice} = useEarningInvoice()
  const {stripeConnection} = useStripeConnection()

  const Content = () => {
    if (
      !organization?.is_stripe_connected ||
      !stripeConnection?.payouts_enabled
    ) {
      return <WarningBox>{t('invoice.stripeRequired')}</WarningBox>
    }

    if (payoutPreview.invoiceTotal > 0) {
      return (
        <InvoiceCreate
          payoutPreview={payoutPreview}
          issueInvoice={issueInvoice}
          t={t}
        />
      )
    }

    return (
      <div>
        {t('invoice.payoutZero')} <b>0.00 €</b>
      </div>
    )
  }

  return (
    <div className="create__event-area mb-20 col-12">
      <div className="body__card-wrapper attendant__wrapper">
        <SectionTitleRow>
          <div>
            <p style={{fontSize: '1.3rem', marginBottom: 6}}>
              <b>{t('invoice.sectionTitle')}</b>
            </p>
            <p style={{color: '#777', marginBottom: 0}}>
              {t('invoice.sectionIntro')}
            </p>
          </div>
        </SectionTitleRow>

        <Content />
      </div>
    </div>
  )
}

function InvoiceCreate(props: {
  payoutPreview: PayoutPreview
  issueInvoice: (invoiceNumber: string) => Promise<boolean>
  t: (key: string) => string
}) {
  const {payoutPreview, issueInvoice, t} = props
  const {
    grossPayout,
    invoiceTotal,
    invoiceSubtotal,
    invoiceVat,
    implicitVatDeductedPercent,
  } = payoutPreview
  const routes = useOrganizationRoutes()
  const navigate = useLocalizedNavigate()
  const {organization} = useOrganization()
  const [invoiceNumber, setInvoiceNumber] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [confirmed, setConfirmed] = useState(false)
  const submitPayoutInvoice = () => {
    setLoading(true)
    issueInvoice(invoiceNumber)
      .then((ok) => {
        if (ok) {
          navigate(routes.invoices)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const submitDisabled = loading || !invoiceNumber || !confirmed

  return (
    <div className="row">
      <div className="col-6">
        <h4>{t('invoice.generateHeading')}</h4>
        <br />
        <div className="singel__input-field mb-15">
          <CompanyEditButton href={routes.settings.root} className="fill__btn">
            {t('invoice.changeCompany')}{' '}
          </CompanyEditButton>
          <CompanyGrid>
            <InfoRow>
              <InfoLabel>{t('invoice.labelCompany')}</InfoLabel>
              <InfoValue>{organization?.company_name}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>{t('invoice.labelEik')}</InfoLabel>
              <InfoValue>{organization?.eik}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>{t('invoice.labelVat')}</InfoLabel>
              <InfoValue>{organization?.vat_number ?? '—'}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>{t('invoice.labelMol')}</InfoLabel>
              <InfoValue>{organization?.mol}</InfoValue>
            </InfoRow>

            <Divider />

            <InfoRow>
              <InfoLabel>{t('invoice.labelCountry')}</InfoLabel>
              <InfoValue>{organization?.country}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>{t('invoice.labelCity')}</InfoLabel>
              <InfoValue>{organization?.city}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>{t('invoice.labelPostal')}</InfoLabel>
              <InfoValue>{organization?.postal_code}</InfoValue>
            </InfoRow>

            <InfoRow style={{gridColumn: '1 / -1'}}>
              <InfoLabel>{t('invoice.labelAddress')}</InfoLabel>
              <InfoValue>{organization?.address_line}</InfoValue>
            </InfoRow>
          </CompanyGrid>

          <SmallNote>{t('invoice.registerNote')}</SmallNote>

          <ConfirmRow>
            <input
              id="confirm-company"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <label htmlFor="confirm-company">
              {t('invoice.confirmCompany')}
            </label>
          </ConfirmRow>
        </div>
      </div>

      <div className="col-6">
        <div className="singel__input-field mb-15">
          <label className="input__field-text">
            {t('invoice.numberLabel')}
          </label>
          <input
            type="text"
            placeholder={t('invoice.numberPh')}
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            disabled={loading}
          />
          <SmallNote>{t('invoice.numberHelp')}</SmallNote>
        </div>

        <div className="singel__input-field mb-15">
          <label className="input__field-text">
            {t('invoice.payoutAmount')} <b>{invoiceTotal.toFixed(2)} €</b>
          </label>
          {implicitVatDeductedPercent != null ? (
            <VatNote>
              {t('invoice.vatImplicit')
                .replace('{pct}', String(implicitVatDeductedPercent))
                .replace('{gross}', grossPayout.toFixed(2))}
              {invoiceVat > 0 ? (
                <>
                  <br />
                  {t('invoice.vatBreakdown')
                    .replace('{sub}', invoiceSubtotal.toFixed(2))
                    .replace('{vat}', invoiceVat.toFixed(2))}
                </>
              ) : null}
            </VatNote>
          ) : invoiceVat > 0 ? (
            <VatNote>
              {t('invoice.vatPlatform')
                .replace('{sub}', invoiceSubtotal.toFixed(2))
                .replace('{vat}', invoiceVat.toFixed(2))
                .replace('{total}', invoiceTotal.toFixed(2))}
            </VatNote>
          ) : (
            <SmallNote style={{marginTop: 8}}>
              {t('invoice.amountMatch')}
            </SmallNote>
          )}
        </div>
      </div>

      <div className="col-12">
        <button
          onClick={submitPayoutInvoice}
          disabled={submitDisabled}
          className={
            submitDisabled ? 'disableBtnStyle w-100' : 'input__btn w-100'
          }
        >
          {t('invoice.submit')}
        </button>

        <HintBox>{t('invoice.afterSubmit')}</HintBox>
      </div>
    </div>
  )
}

const WarningBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed var(--clr-border-1);
  border-radius: 4px;
  color: #a94442;
  background: #f2dede;
`

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--clr-border-1);
`

const SmallNote = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #777;
`

const VatNote = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #444;
  background: #f0f7ff;
  border: 1px solid #c5dcf5;
  border-radius: 6px;
`

const HintBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed var(--clr-border-1);
  border-radius: 8px;
  color: #666;
  background: #fafafa;
`

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`

const InfoRow = styled.div`
  display: grid;
  gap: 4px;
`

const InfoLabel = styled.div`
  font-size: 12px;
  opacity: 0.75;
`

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
`

const Divider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 4px 0;
`
const CompanyEditButton = styled.a`
  font-size: 14px;
  padding: 5px;
  height: auto;
  margin-bottom: 10px;

  &:hover {
    padding: 5px;
  }
`

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
   margin-top: 15px;
  input {
   
        width: 20px;
    height: 20px;
}
  }

  label {
    font-size: 13px;
    line-height: 1.35;
    opacity: 0.9;
    cursor: pointer;
    user-select: none;
    font-weight: bold;
  }
`
